import type { ElementType } from 'react';
import i18n from '@/i18n';
import type { NavItemDef, NavSection } from '@/components/layout/sidebar-primitives';

/**
 * The nav key constants (#2109 R1 lane L6; rulings brief B7).
 *
 * Every nav label, group label, tab-bar slot and the eyebrow of a page
 * without a nav row lives in `src/locales/en/nav.json`, and the three nav
 * modules, `BottomNav` and `PageHead` reach it ONLY through these constants:
 * a nav row and the page it opens share one key, so the eyebrow-parity
 * guard resolves both sides against the English catalog and the §4.4 rule
 * ("the eyebrow is the nav label, character for character") survives
 * translation without a second hand-kept list.
 *
 * The values are full `namespace:key` paths so `i18n.t(NAV_KEY.x)` is typed
 * against the generated resources (`src/types/resources.d.ts`) and resolves
 * from any namespace binding. Ids never rename — R3/R4 convert page eyebrows
 * to `eyebrowKey={NAV_KEY.<id>}` one page at a time and depend on them.
 */
export const NAV_KEY = {
  // ── sidebar rows ──────────────────────────────────────────────────────
  today: 'nav:items.today',
  unreads: 'nav:items.unreads',
  health: 'nav:items.health',
  grants: 'nav:items.grants',
  tasks: 'nav:items.tasks',
  calendar: 'nav:items.calendar',
  announcements: 'nav:items.announcements',
  support: 'nav:items.support',
  resources: 'nav:items.resources',
  settings: 'nav:items.settings',
  help: 'nav:items.help',
  applications: 'nav:items.applications',
  committee: 'nav:items.committee',
  importFamilies: 'nav:items.importFamilies',
  forms: 'nav:items.forms',
  referralCodes: 'nav:items.referralCodes',
  partnerHospitals: 'nav:items.partnerHospitals',
  grantRequests: 'nav:items.grantRequests',
  awards: 'nav:items.awards',
  reimbursements: 'nav:items.reimbursements',
  cardCharges: 'nav:items.cardCharges',
  families: 'nav:items.families',
  users: 'nav:items.users',
  polls: 'nav:items.polls',
  adminDashboard: 'nav:items.adminDashboard',
  systemHealth: 'nav:items.systemHealth',
  platformStats: 'nav:items.platformStats',
  translationReports: 'nav:items.translationReports',
  conversationReview: 'nav:items.conversationReview',
  adminConversations: 'nav:items.adminConversations',
  handoffOversight: 'nav:items.handoffOversight',
  userFeedback: 'nav:items.userFeedback',
  accessControl: 'nav:items.accessControl',
  platformSettings: 'nav:items.platformSettings',
  handoffConfig: 'nav:items.handoffConfig',
  publishPolicy: 'nav:items.publishPolicy',
  paymentAccounts: 'nav:items.paymentAccounts',
  // ── group labels ──────────────────────────────────────────────────────
  groupCare: 'nav:groups.care',
  groupNetwork: 'nav:groups.network',
  groupTrfApplications: 'nav:groups.trfApplications',
  groupGrants: 'nav:groups.grants',
  groupManage: 'nav:groups.manage',
  groupCommunity: 'nav:groups.community',
  groupSystem: 'nav:groups.system',
  groupOversee: 'nav:groups.oversee',
  groupConfigure: 'nav:groups.configure',
  // ── mobile tab bar (BottomNav) ────────────────────────────────────────
  tabHome: 'nav:tabs.home',
  tabChat: 'nav:tabs.chat',
  tabMore: 'nav:tabs.more',
  // ── eyebrows of pages with no nav row ─────────────────────────────────
  notFound: 'nav:eyebrows.notFound',
} as const;

export type NavKey = (typeof NAV_KEY)[keyof typeof NAV_KEY];

/** The active locale's label for a nav key, through the i18next singleton. */
export function navLabel(key: NavKey): string {
  return i18n.t(key);
}

/** A nav item that carries its key beside its resolved label. */
export interface KeyedNavItemDef extends NavItemDef {
  nameKey: NavKey;
}

/** A nav section whose group label carries its key. */
export interface KeyedNavSection extends NavSection {
  labelKey?: NavKey;
}

/**
 * Declare one nav row. `name` is a getter, so it is resolved when READ — by
 * a sidebar render, the help page, the parity tests — never when the module
 * that declares the row is imported. That keeps the module-level trays
 * (`SIDEBAR_FOOTER_ITEMS`, `TRFER_FOOTER_ITEMS`) correct after a language
 * change without turning them into functions, and it keeps `item.name`
 * working for every existing consumer. Under the test locale (`en`, rulings
 * brief B24) every read returns the English label.
 */
export function navItem(
  nameKey: NavKey,
  href: string,
  icon: ElementType,
  extra: { end?: boolean } = {},
): KeyedNavItemDef {
  return {
    nameKey,
    href,
    icon,
    ...extra,
    get name() {
      return navLabel(nameKey);
    },
  };
}

/** Declare one grouped section; the label resolves when read, like `navItem`. */
export function navGroup(labelKey: NavKey, items: NavItemDef[]): KeyedNavSection {
  return {
    labelKey,
    items,
    get label() {
      return navLabel(labelKey);
    },
  };
}
